import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Instagram, Youtube, Search, MessageCircle, Menu, X, Lock } from "lucide-react";
import Crest from "@/components/Crest";
import { MENU, SEARCH_INDEX, WHATSAPP_URL, SOCIALS } from "@/data/content";
import { api } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

export default function Header({ onPrayer }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [clergyIndex, setClergyIndex] = useState([]);

  useEffect(() => {
    api.get("/clergy")
      .then((r) => setClergyIndex(r.data.map((c) => ({ label: `${c.titulo} ${c.nome}`, to: `/clero/${c.id}` }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const results = query.trim()
    ? [...SEARCH_INDEX, ...clergyIndex].filter((s) => s.label.toLowerCase().includes(query.toLowerCase()))
    : [];

  const go = (id) => {
    setMobileOpen(false);
    setSearchOpen(false);
    setQuery("");
    if (id === "documentos" || id === "comunidades") {
      navigate(`/${id}`);
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    scrollToId(id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="hidden md:flex items-center justify-between bg-[#2E070D] px-6 lg:px-12 py-2 text-[#FBF9F4]">
        <div className="flex items-center gap-4">
          <img src="/brasao-afci-oficial.webp" alt="Brasão oficial da Anglican Free Communion International" className="h-8 w-auto object-contain" />
          <a data-testid="social-facebook" href={SOCIALS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="opacity-70 hover:opacity-100 hover:text-[#C5A059] transition-opacity"><Facebook className="h-3.5 w-3.5" /></a>
          <a data-testid="social-instagram" href={SOCIALS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="opacity-70 hover:opacity-100 hover:text-[#C5A059] transition-opacity"><Instagram className="h-3.5 w-3.5" /></a>
          <a data-testid="social-youtube" href={SOCIALS.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="opacity-70 hover:opacity-100 hover:text-[#C5A059] transition-opacity"><Youtube className="h-3.5 w-3.5" /></a>
          <span className="overline-label text-[#C5A059]/70 hidden lg:inline">Anglican Free Communion International</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-prayer-request-button"
            onClick={onPrayer}
            className="overline-label bg-[#C5A059] text-[#2E070D] px-4 py-1.5 hover:bg-[#D4AF37] transition-colors"
          >
            Pedido de Oração
          </button>
          <a
            data-testid="nav-whatsapp-button"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="overline-label flex items-center gap-1.5 border border-[#C5A059]/50 px-4 py-1.5 text-[#FBF9F4] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp Pastoral
          </a>
          <Link data-testid="nav-admin-login-button" to="/admin" aria-label="Área administrativa" className="opacity-60 hover:opacity-100 hover:text-[#C5A059] transition-opacity">
            <Lock className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <div
        className={`flex items-center justify-between px-4 sm:px-8 lg:px-12 py-3 transition-colors duration-500 ${
          scrolled ? "bg-[#FBF9F4]/92 backdrop-blur-xl border-b border-[#E5DEC9] text-[#1C1917]" : "bg-gradient-to-b from-[#2E070D]/80 via-[#2E070D]/40 to-transparent text-[#FBF9F4]"
        }`}
      >
        <button data-testid="nav-logo" onClick={() => go("inicio")} className="flex items-center gap-3 text-left">
          <img src="/brasao-dacb-oficial.webp" alt="Brasão oficial da Diocese Anglicana Católica do Brasil" className="h-12 w-auto shrink-0 object-contain" />
          <span>
            <span className="font-display block text-sm sm:text-base font-bold tracking-wide leading-tight">Diocese Anglicana Católica do Brasil</span>
            <span className={`overline-label block ${scrolled ? "text-[#4A0E17]/60" : "text-[#C5A059]"}`}>DACB • Fé • Tradição • Missão</span>
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-6">
          {MENU.map((m) => (
            <a
              key={m.id}
              data-testid={m.testid}
              href={`/#${m.id}`}
              onClick={(e) => {
                e.preventDefault();
                go(m.id);
              }}
              className={`overline-label transition-colors ${scrolled ? "hover:text-[#4A0E17] text-[#1C1917]/70" : "hover:text-[#C5A059] text-[#FBF9F4]/85"}`}
            >
              {m.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative" ref={searchRef}>
            <button
              data-testid="nav-search-toggle"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="Buscar"
              className={`p-2 transition-colors ${scrolled ? "hover:text-[#4A0E17]" : "hover:text-[#C5A059]"}`}
            >
              <Search className="h-4.5 w-4.5 h-5 w-5" />
            </button>
            <AnimatePresence>
              {searchOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 top-12 w-72 bg-[#FBF9F4] border border-[#E5DEC9] shadow-[0_20px_50px_rgba(46,7,13,0.18)] p-3"
                >
                  <input
                    data-testid="nav-search-input"
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar no site..."
                    className="w-full bg-white border border-[#E5DEC9] px-3 py-2 text-sm text-[#1C1917] focus:outline-none focus:border-[#C5A059]"
                  />
                  {results.length > 0 && (
                    <ul className="mt-2 divide-y divide-[#E5DEC9]">
                      {results.map((r) => (
                        <li key={r.id || r.to}>
                          <button
                            data-testid={`search-result-${r.id || r.to}`}
                            onClick={() => (r.to ? (setSearchOpen(false), setQuery(""), navigate(r.to)) : go(r.id))}
                            className="w-full text-left px-2 py-2 text-sm text-[#4A0E17] hover:bg-[#F4EFE6] transition-colors"
                          >
                            {r.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            data-testid="nav-mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            className="xl:hidden p-2"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-[#2E070D] text-[#FBF9F4] border-t border-[#C5A059]/20 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col">
              {MENU.map((m) => (
                <a
                  key={m.id}
                  data-testid={`mobile-${m.testid}`}
                  href={`/#${m.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    go(m.id);
                  }}
                  className="text-left font-display text-lg py-3 border-b border-[#C5A059]/10 hover:text-[#C5A059] transition-colors"
                >
                  {m.label}
                </a>
              ))}
              <div className="flex gap-3 pt-4">
                <button data-testid="mobile-prayer-button" onClick={() => { setMobileOpen(false); onPrayer(); }} className="overline-label bg-[#C5A059] text-[#2E070D] px-4 py-2">Pedido de Oração</button>
                <a data-testid="mobile-whatsapp-button" href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="overline-label border border-[#C5A059]/50 px-4 py-2">WhatsApp</a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
